// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import Superior36Chart from "./Superior36Chart/Superior36Chart";
import Superior36ChartLegend from "./Superior36ChartLegend/Superior36ChartLegend";
import GazeGraph from "../../../GazeGraph/GazeGraph";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import {
  getChartSpots,
  getGazeChartData,
  isTerminatedExamReport,
} from "src/utils/exams/examReportsHelper";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

// Constants
import { examCardEnums } from "src/constants/enums";

const Superior36Graph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.VISUAL_FIELD_SUPERIOR_36.sectionName,
      }),
    [exam, examReport]
  );

  const dimensions = useComponentDimensions({
    componentId: "#superior36-container",
  });

  const superior36Spots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const gazeSpots = useMemo(() => {
    const { gazeSpots } = getGazeChartData(examReport);
    return gazeSpots ?? [];
  }, [examReport]);

  return (
    <Grid container gap="20px" id="superior36-container">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt="20px"
      >
        <Superior36Chart
          superior36Spots={superior36Spots}
          containerSize={dimensions?.dynamicWidth}
        />
      </Grid>
      <Grid item xs={12}>
        <Superior36ChartLegend superior36Spots={superior36Spots} />
      </Grid>
      <Grid item xs={12}>
        <GazeGraph gazeSpots={gazeSpots} />
      </Grid>
    </Grid>
  );
};

export default Superior36Graph;
